// ============================================================================
// LO QUE SABE TU BOT (T-CON-9) — las fichas de conocimiento del negocio.
// Cubre R-CON-01 (fichas por tema, que se encienden y se apagan) y R-CON-02
// (la baranda anti-precios se entiende, no se sufre).
//
// ⛔⛔ LA TRAMPA DEL TRAMO, Y POR QUÉ ESTA PANTALLA ESTÁ HECHA ASÍ.
// El motor reparte cada ficha por su **título EXACTO**
// (`worker/src/conectores/entrenamiento.ts:246`, contra `CAMPOS_DE_FICHA`).
// Renombrar «garantia» a «Garantía» la saca de su campo: el bot deja de saber
// la garantía **sin un solo error, sin un log y sin que nadie se entere**. Por
// eso los ocho temas conocidos se pintan RECORRIENDO `TEMAS_CONOCIDOS`
// (`tipos.ts`) —nombre en español arriba, título técnico DEBAJO Y SIN CAJA— y
// `guardarFicha` no manda `titulo` cuando la ficha ya existe. Las dos cosas las
// mide V-34, que además exige que `TEMAS_CONOCIDOS` y `CAMPOS_DE_FICHA` sean la
// misma lista: copiada a mano, la pantalla ofrecería un tema que el motor manda
// a `extra` y nadie lo notaría.
// Las fichas que el dueño inventa SÍ llevan título libre — el motor las lee por
// `extra`, que para eso existe.
//
// ⛔ LA BARANDA ANTI-PRECIOS NO ESTÁ AQUÍ Y NO PUEDE ESTARLO. Vive en el trigger
// `chatai_app.guard_conocimiento` (`0004:86-97`, errcode `P0004`), que es la
// puerta por la que pasan esta pantalla Y quien escriba por la API con la
// sesión del dueño. Aquí solo se TRADUCE el rechazo y se ofrece la salida
// legítima (`permite_precios`). Copiar la expresión regular al navegador daría
// dos verdades, y la del navegador se la salta cualquiera. Lo miden V-35 y V-36.
//
// ⚠️ APAGAR NO BORRA (R-CON-01). El motor solo lee `activo`; un `delete` haría
// que apagar «garantía» para revisarla se llevara el texto sin vuelta atrás.
// Lo mide V-33.
// ============================================================================
import { useCallback, useEffect, useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { Aviso, Boton, Campo, Interruptor, SkeletonLista } from "./piezas.tsx";
import {
  cambiarActivoFicha, cargarEquipo, esRechazoPorPrecios, esChoqueDeTitulo, explicarError, guardarFicha,
  leerConocimiento,
} from "./datos.ts";
import { TEMAS_CONOCIDOS, esTemaConocido } from "./tipos.ts";
import type { ContextoPanel, FichaConocimiento } from "./tipos.ts";

/**
 * Un renglón de la pantalla. Puede tener ficha (existe en la base) o no (un tema
 * conocido que el dueño todavía no ha escrito: se ofrece vacío, porque
 * justamente el agujero que abre R-CON-01 es que `web` y `telefono` llevaban
 * meses en `null` sin dónde escribirse).
 */
interface Renglon {
  clave: string;
  /** El título TÉCNICO: la llave del motor. Para un tema propio nuevo, "". */
  titulo: string;
  /** Lo que ve el dueño. Para un tema conocido, el nombre en español. */
  nombre: string;
  ayuda?: string;
  conocido: boolean;
  ficha: FichaConocimiento | null;
}

function renglones(fichas: FichaConocimiento[], propiosNuevos: string[]): Renglon[] {
  const porTitulo = new Map(fichas.map((f) => [f.titulo, f]));
  // ⛔ RECORRIDO de la lista, no ocho bloques a mano: es lo que hace que V-34
  // pueda comprobar «lo que la pantalla pinta ⊆ lo que el motor coloca».
  const conocidos: Renglon[] = TEMAS_CONOCIDOS.map((t) => ({
    clave: porTitulo.get(t.titulo)?.id ?? `tema:${t.titulo}`,
    titulo: t.titulo, nombre: t.nombre, ayuda: t.ayuda, conocido: true,
    ficha: porTitulo.get(t.titulo) ?? null,
  }));
  const propios: Renglon[] = fichas
    .filter((f) => !esTemaConocido(f.titulo))
    .map((f) => ({ clave: f.id, titulo: f.titulo, nombre: f.titulo, conocido: false, ficha: f }));
  const nuevos: Renglon[] = propiosNuevos.map((clave) => ({
    clave, titulo: "", nombre: "", conocido: false, ficha: null,
  }));
  return [...conocidos, ...propios, ...nuevos];
}

/** «hace un momento» no: aquí importa la FECHA, que es lo que el dueño coteja. */
function fecha(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" });
}

export function Conocimiento({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, usuarioId, rol } = ctx;
  const puedeEditar = rol === "dueno" || rol === "victorix";

  const [fichas, setFichas] = useState<FichaConocimiento[] | null>(null);
  const [propiosNuevos, setPropiosNuevos] = useState<string[]>([]);
  const [nombres, setNombres] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    try {
      setFichas(await leerConocimiento(sb, tenantId));
      setError(null);
    } catch (e) {
      setError(explicarError(e));
    }
  }, [sb, tenantId]);

  useEffect(() => { void recargar(); }, [recargar]);

  // Quién cambió cada ficha. ⚠️ `equipo_del_negocio` es solo-dueño (`0033:99`):
  // para un agente esto revienta, y no es un fallo de la pantalla — se queda sin
  // el nombre y se muestra solo la fecha. Callarlo con un `catch` vacío es lo
  // correcto AQUÍ y solo aquí: es un adorno, no el dato.
  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const { miembros } = await cargarEquipo(sb, tenantId);
        if (!vivo) return;
        setNombres(new Map(miembros.map((m) => [m.user_id, m.nombre ?? m.email ?? ""])));
      } catch { /* sin nombres: se muestra la fecha sola */ }
    })();
    return () => { vivo = false; };
  }, [sb, tenantId]);

  /** La fila que devolvió la base entra en la lista; nunca lo que se escribió. */
  function asentar(f: FichaConocimiento, claveNueva?: string) {
    setFichas((xs) => {
      const base = xs ?? [];
      return base.some((x) => x.id === f.id)
        ? base.map((x) => (x.id === f.id ? f : x))
        : [...base, f];
    });
    if (claveNueva) setPropiosNuevos((ns) => ns.filter((n) => n !== claveNueva));
  }

  const lista = fichas ? renglones(fichas, propiosNuevos) : [];
  const activos = new Set(
    (fichas ?? []).filter((f) => f.activo).map((f) => f.titulo.trim().toLowerCase()),
  );

  return (
    <div className="h-full flex flex-col min-w-0">
      <header className="shrink-0 px-4 sm:px-6 pt-4 pb-3 border-b border-borde-suave">
        <h1 className="text-[19px] font-semibold tracking-tight">Lo que sabe tu bot</h1>
        <p className="text-[12.5px] text-tinta-2 mt-0.5">
          Fichas por tema. Lo que esté encendido, el bot lo puede contar.
        </p>
      </header>

      <div className="flex-1 overflow-y-auto scroll-fino px-4 sm:px-6 py-4">
        <div className="max-w-[680px] mx-auto w-full space-y-3">
          {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
          {!puedeEditar && (
            <Aviso tono="alerta">
              Puedes ver lo que sabe el bot, pero cambiarlo es cosa del dueño del negocio.
            </Aviso>
          )}
          {!fichas && <SkeletonLista filas={4} />}

          {fichas && (<>
            <p className="text-[12.5px] text-tinta-2 leading-snug px-1">
              Los precios de tus productos NO van aquí: viven en tu catálogo, y el bot los saca de
              allá. Si los escribes en dos sitios, tarde o temprano te da el que ya no es.
            </p>

            {lista.map((r) => (
              <Fila
                key={r.clave} r={r} puedeEditar={puedeEditar}
                nombreDe={(id) => (id ? nombres.get(id) ?? null : null)}
                titulosActivos={activos}
                onGuardar={(datos) => guardarFicha(sb, tenantId, datos, usuarioId)}
                onActivo={(id, activo) => cambiarActivoFicha(sb, tenantId, id, activo, usuarioId)}
                onAsentar={asentar}
                onQuitarBorrador={() => setPropiosNuevos((ns) => ns.filter((n) => n !== r.clave))}
              />
            ))}

            {/* ⚠️ Aquí NO hay estado vacío, y es a propósito: los ocho temas
                conocidos se pintan SIEMPRE, escritos o no. Ese es el agujero que
                R-CON-01 cierra —`web` y `telefono` llevaban meses en `null`
                porque no había dónde escribirlos—, y un «no hay nada todavía»
                lo dejaría igual de invisible. */}

            {puedeEditar && (
              <div className="pt-1 pb-4">
                <Boton onClick={() => setPropiosNuevos((ns) => [...ns, `propio:${Date.now()}:${ns.length}`])}>
                  <span className="inline-flex items-center gap-1.5"><Plus size={15} /> Añadir un tema tuyo</span>
                </Boton>
                <p className="text-[12.5px] text-tinta-2 leading-snug mt-1.5 px-1">
                  Para lo que no cabe en los temas de arriba: promociones vigentes, parqueadero,
                  qué hacer si el producto llega dañado…
                </p>
              </div>
            )}
          </>)}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Fila({
  r, puedeEditar, nombreDe, titulosActivos, onGuardar, onActivo, onAsentar, onQuitarBorrador,
}: {
  r: Renglon;
  puedeEditar: boolean;
  nombreDe: (id: string | null) => string | null;
  titulosActivos: Set<string>;
  onGuardar: (d: { id?: string; titulo: string; contenido: string; permite_precios: boolean })
    => Promise<FichaConocimiento>;
  onActivo: (id: string, activo: boolean) => Promise<FichaConocimiento>;
  onAsentar: (f: FichaConocimiento, claveNueva?: string) => void;
  onQuitarBorrador: () => void;
}) {
  const f = r.ficha;
  const [titulo, setTitulo] = useState(r.conocido ? r.titulo : (f?.titulo ?? ""));
  const [contenido, setContenido] = useState(f?.contenido ?? "");
  const [precios, setPrecios] = useState(f?.permite_precios ?? false);
  const [rechazadaPorPrecios, setRechazada] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [fallo, setFallo] = useState<string | null>(null);

  const activo = f?.activo ?? false;
  const sucio = contenido !== (f?.contenido ?? "")
    || precios !== (f?.permite_precios ?? false)
    || (!r.conocido && titulo !== (f?.titulo ?? ""));

  // El aviso de título repetido: la PANTALLA lo dice antes, la BASE lo impide
  // siempre (índice único de la migración 0049). Sin el índice esto sería un
  // consejo; sin el aviso, el dueño se estrellaría con un error de base.
  const limpio = titulo.trim().toLowerCase();
  const repetido = !r.conocido && limpio !== "" && limpio !== (f?.titulo ?? "").toLowerCase()
    && titulosActivos.has(limpio);

  // ⛔ El interruptor de precios NO se ofrece de entrada: aparece cuando la base
  // rechazó (para que la salida legítima se vea justo cuando hace falta) o
  // cuando ya está encendido (para poder apagarlo). Ofrecérselo a todo el mundo
  // sería invitar a meter precios aquí, que es justo lo que R-CON-02 evita.
  const ofrecerPrecios = rechazadaPorPrecios || precios;

  async function guardar() {
    if (guardando || !contenido.trim() || (!r.conocido && !titulo.trim()) || repetido) return;
    setGuardando(true); setFallo(null);
    try {
      // ⛔ El título que viaja es el TÉCNICO en los temas conocidos. Y para una
      // ficha que ya existe, `guardarFicha` no lo manda en absoluto.
      const g = await onGuardar({
        id: f?.id, titulo: r.conocido ? r.titulo : titulo.trim(),
        contenido, permite_precios: precios,
      });
      onAsentar(g, f ? undefined : r.clave);
      // Se pinta lo que devolvió la BASE, no lo que se escribió.
      setContenido(g.contenido); setPrecios(g.permite_precios); setTitulo(g.titulo);
      setRechazada(false); setGuardado(true);
    } catch (e) {
      if (esRechazoPorPrecios(e)) {
        // ⛔ NUNCA `explicarError(e)` ni `e.message` aquí: el texto crudo del
        // trigger nombra la columna `permite_precios`, que al dueño no le dice
        // nada, y no le enseña la salida. Se traduce y se ofrece el interruptor.
        setRechazada(true);
        setFallo(null);
      } else if (esChoqueDeTitulo(e)) {
        // El aviso previo (V-37) cubre lo que la pantalla ve; esto cubre lo que
        // no vio — otra pestaña, u otro usuario, que encendió ese título mientras
        // este formulario estaba abierto.
        setFallo("Ya tienes otra ficha encendida con ese mismo título. Apaga esa primero, o cámbiale el nombre a una de las dos: el bot se quedaría con una sola y no se sabe con cuál.");
      } else {
        setFallo(explicarError(e));
      }
    } finally {
      setGuardando(false);
    }
  }

  async function alternarActivo(v: boolean) {
    if (!f) return;
    setFallo(null);
    try {
      const g = await onActivo(f.id, v);
      onAsentar(g);
    } catch (e) {
      // ⛔ Tres rechazos distintos, tres explicaciones. Lo que NO puede pasar es
      // que al dueño le llegue el texto crudo de Postgres: el del índice viene
      // en inglés y nombra `conocimiento_titulo_unico_activo`.
      setFallo(
        esRechazoPorPrecios(e)
          ? "Esta ficha tiene un precio escrito y la base no la deja mover hasta que lo aclares: ábrela, enciende «esto sí es un precio» y guárdala."
          : esChoqueDeTitulo(e)
          ? "Ya tienes otra ficha encendida con ese mismo título. Apaga esa primero, o cámbiale el nombre a una de las dos: el bot se quedaría con una sola y no se sabe con cuál."
          : explicarError(e));
    }
  }

  return (
    <article
      data-ficha={r.conocido ? r.titulo : (f?.titulo ?? r.clave)}
      data-conocido={r.conocido ? "si" : "no"}
      className="border border-borde rounded-xl bg-superficie p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {r.conocido ? (<>
            {/* ⛔⛔ TEXTO, NO CAJA. El título es la llave del motor: aquí se
                enseña y no se edita por ningún camino (V-34). */}
            <h2 className="text-[14px] font-semibold">{r.nombre}</h2>
            <p className="text-[12px] text-tinta-2 mt-0.5">
              Tema del bot: <code data-titulo-fijo={r.titulo} className="bg-tenue px-1 py-0.5 rounded">{r.titulo}</code>
            </p>
          </>) : (
            <h2 className="text-[14px] font-semibold">{f ? f.titulo : "Tema nuevo"}</h2>
          )}
        </div>
        {f && (
          <span className="shrink-0">
            <Interruptor
              testId={`activo:${r.titulo}`}
              activo={activo} deshabilitado={!puedeEditar}
              onCambiar={(v) => void alternarActivo(v)}
              etiqueta={activo ? "Encendida" : "Apagada"}
            />
          </span>
        )}
      </div>

      {!r.conocido && (
        <Campo
          testId={`titulo:${r.clave}`}
          etiqueta="Título del tema" requerido maxLength={60}
          placeholder="Parqueadero"
          ayuda="Como quieras llamarlo. El bot lo usa para saber de qué habla la ficha."
          valor={titulo} deshabilitado={!puedeEditar || !!f}
          invalido={repetido}
          onCambiar={(v) => { setTitulo(v); setGuardado(false); }}
        />
      )}

      <div className="py-2">
        <label className="block text-[14px] font-medium mb-1" htmlFor={`c-${r.clave}`}>
          Qué debe saber el bot
        </label>
        <textarea
          id={`c-${r.clave}`} data-campo={`contenido:${r.titulo || r.clave}`}
          value={contenido} disabled={!puedeEditar}
          rows={3} maxLength={2000}
          placeholder={r.ayuda ?? "Escríbelo como se lo dirías a un cliente."}
          onChange={(e) => { setContenido(e.target.value); setGuardado(false); }}
          className="w-full px-3 py-2 rounded-lg bg-elevado text-tinta text-sm border border-borde
            focus:outline-none focus:ring-2 focus:ring-marca/40 disabled:opacity-50"
        />
        {r.ayuda && <p className="text-[12.5px] text-tinta-2 leading-snug mt-1">{r.ayuda}</p>}
      </div>

      {repetido && (
        <Aviso tono="alerta">
          Ya tienes una ficha encendida que se llama así. Si creas otra, el bot se queda con UNA
          sola y cuál es depende del orden de la base. Ponle otro nombre, o apaga la que ya tienes.
        </Aviso>
      )}

      {rechazadaPorPrecios && (
        <Aviso tono="alerta">
          Eso parece un precio, y los precios de tus productos viven en tu catálogo: si están en dos
          sitios, tarde o temprano el bot da el que ya no es. Si de verdad es un precio de esta
          ficha —el costo de un envío, por ejemplo—, enciende el interruptor de abajo y guarda otra vez.
        </Aviso>
      )}

      {ofrecerPrecios && (
        <Interruptor
          testId={`precios:${r.titulo || r.clave}`}
          activo={precios} deshabilitado={!puedeEditar}
          onCambiar={(v) => { setPrecios(v); setGuardado(false); }}
          etiqueta="Esto sí es un precio de verdad (por ejemplo, el costo del envío)"
          detalle="Queda anotado quién lo activó. Úsalo solo para precios que NO estén en tu catálogo."
        />
      )}

      {fallo && <Aviso tono="peligro" onCerrar={() => setFallo(null)}>{fallo}</Aviso>}

      {puedeEditar && (
        <div className="flex items-center gap-3 pt-1">
          <Boton tono="marca" onClick={() => void guardar()}
            disabled={guardando || !sucio || !contenido.trim() || (!r.conocido && !titulo.trim()) || repetido}>
            {guardando ? "Guardando…" : f ? "Guardar" : "Crear ficha"}
          </Boton>
          {!f && (
            <button type="button" onClick={onQuitarBorrador}
              className="text-[13px] text-tinta-2 hover:text-tinta inline-flex items-center gap-1 cursor-pointer">
              <Trash2 size={14} /> Descartar
            </button>
          )}
          {guardado && (
            <span className="entra flex items-center gap-1 text-[13px] text-ok">
              <Check size={15} /> Guardado
            </span>
          )}
          {f && (
            <span className="ml-auto text-[12px] text-tinta-2">
              {fecha(f.actualizado_en)}
              {nombreDe(f.actualizado_por) && ` · ${nombreDe(f.actualizado_por)}`}
            </span>
          )}
        </div>
      )}
    </article>
  );
}
